import { authHandlers, callApi, useOnSuccessQuery, useQuery } from '@wyz/utils';
import { FullPageSpinner } from '@wyz/ui';
import * as React from 'react';
import { I18nConfig, IGlobalConfig } from '~/common/types';
// eslint-disable-next-line @wyz/module/no-relative-import
import { ConfigContext } from './contexts';
import { initGTM } from '~/common/helpers';

export function ConfigProvider(props: React.PropsWithChildren) {
  const call = callApi({});

  const i18nConfig = useQuery<I18nConfig>({
    queryKey: ['i18n-config'],
    queryFn: () =>
      call<I18nConfig>('/i18n_config.json', {
        authless: true,
      }),
    gcTime: Infinity,
    meta: {},
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const globalConfig = useQuery<IGlobalConfig>({
    queryKey: ['global-config'],
    queryFn: () =>
      call<IGlobalConfig>('/config.json', {
        authless: true,
      }),
    gcTime: Infinity,
    meta: {},
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  useOnSuccessQuery(globalConfig, (config) => {
    // eslint-disable-next-line no-console
    console.log('Config loaded', config);
    if (config.tenant) {
      authHandlers().setTenant(config.tenant);
    }
    if (config.tenantName) {
      // No need for third party libs for only the title,
      // if we will need more dynamic stuff in the head tag, we'll start using react helmet
      document.title = config.tenantName;
    }
    if (config.gtmToken) {
      initGTM(config.gtmToken);
    }
  });

  if (!i18nConfig.isSettled || !globalConfig.isSettled) {
    return <FullPageSpinner />;
  }

  if (!globalConfig.isSuccess || !i18nConfig.isSuccess) {
    return <>Failed to fetch config</>;
  }

  return (
    <ConfigContext.Provider
      value={{
        ...globalConfig.data,
        tenantCode: globalConfig.data.tenant,
        usetifulEnabled: ['prod', 'preprod'].includes(
          globalConfig.data.environment,
        ),
        i18nConfig: i18nConfig.data,
        baseAppUrl: `${location.origin}${globalConfig.data.routingBasename ?? ''}`,
      }}
    >
      {props.children}
    </ConfigContext.Provider>
  );
}
export const useConfig = () => {
  const context = React.useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigContext');
  }
  return context;
};
